import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Phone, MapPin, Globe, Search, ChevronDown, Map, Navigation, Plus } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import AuthPageLayout from '../components/auth/AuthPageLayout';
import EmailDomainSuggestInput from '../components/auth/EmailDomainSuggestInput';
import apiService from '../utils/apiService';
import NewtonsCradleLoader from '../components/common/NewtonsCradleLoader';
import { translations } from '../utils/translations';

const labelClass = 'mb-1.5 block text-sm font-semibold text-gray-800';
const inputClass =
  'block w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-3 text-sm text-gray-900 placeholder-gray-400 outline-none transition focus:border-[#e11d23] focus:ring-2 focus:ring-[#e11d23]/15';
const inputWithIconRightClass =
  'block w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 placeholder-gray-400 outline-none transition focus:border-[#e11d23] focus:ring-2 focus:ring-[#e11d23]/15';

const Register = () => {
  // Referral link comes in as .../register?referral=<AffiliateCode>; captured once on
  // mount and sent through with the registration request so the backend can link the new
  // affiliate to this parent (AffiliateUser.AffiliateParentId + AffiliateReferSubPartner row).
  const [searchParams] = useSearchParams();
  const referralCode = (searchParams.get('referral') || '').trim();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    countryId: 0, // Will be set after API loads
    countryCode: '', // Will be set after API loads
    mobileNumber: '',
    state: '',
    city: ''
  });





  // Will be replaced with Google Maps API integration
  const [locationsByCountry, setLocationsByCountry] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const countrySearchRef = useRef(null);
  const countryDropdownRef = useRef(null);

  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [, setSelectedLocation] = useState(null);
  const [mapSearchTerm, setMapSearchTerm] = useState('');
  const [apiCountries, setApiCountries] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const hasCountriesLoadedRef = useRef(false);
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  
  // Password strength validation states
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    hasLetter: false,
    hasNumber: false,
    hasSpecial: false
  });
  
  const t = translations[selectedLanguage] || translations.English;

  useEffect(() => {
    // Get language from localStorage
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage && translations[savedLanguage]) {
      setSelectedLanguage(savedLanguage);
    }
    
    // Prevent multiple API calls
    if (hasCountriesLoadedRef.current) return;
    hasCountriesLoadedRef.current = true;
    
    fetchCountries();
  }, []);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail;
      if (newLanguage && translations[newLanguage]) {
        setSelectedLanguage(newLanguage);
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const fetchCountries = async () => {

    setIsLoadingCountries(true);
    try {
      
      const response = await apiService.getCountries();
      
      
      if (response.success) {
        const countriesData = response.data || [];
        
        
        setApiCountries(countriesData);
        
        // Don't auto-select first country - let user choose
        if (countriesData && countriesData.length > 0) {

        } else {
        }
      } else {
        // Error handling
        setApiCountries([]);
      }
    } catch (error) {
      // Error handling
      setApiCountries([]);
    } finally {
      setIsLoadingCountries(false);
      
    }
  };

  const fetchLocations = async (countryId) => {
    // Will be replaced with Google Maps API integration
    
    setLocationsByCountry(prev => ({
      ...prev,
      [countryId]: []
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setIsCountryDropdownOpen(false);
        setCountrySearchTerm('');
      }

    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Password strength validation
    if (name === 'password') {
      const strength = {
        length: value.length >= 8,
        hasLetter: /[a-zA-Z]/.test(value),
        hasNumber: /[0-9]/.test(value),
        hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value)
      };
      setPasswordStrength(strength);
    }
  };

  const handleCountryChange = async (selectedCountry) => {
    const countryId = selectedCountry.countryId || selectedCountry.id;
    
    setFormData({
      ...formData,
      countryId: countryId,
      countryCode: `+${selectedCountry.countryPhoneCode || selectedCountry.phoneCode || selectedCountry.code || '92'}`,
      state: '', // Clear state when country changes
      city: ''   // Clear city when country changes
    });
    
    setIsCountryDropdownOpen(false);
    setCountrySearchTerm('');
    
    // Fetch locations for the selected country
    await fetchLocations(countryId);
  };

  const filteredCountries = Array.isArray(apiCountries) ? apiCountries.filter(country =>
    country && country.countryName && typeof country.countryName === 'string' && 
    country.countryName.toLowerCase().includes(countrySearchTerm.toLowerCase())
  ) : [];
  
  





  const handleMapLocationSelect = (location) => {
    // Only set if location has valid state and city
    const validState = location.state && location.state.trim();
    const validCity = location.city && location.city.trim();
    
    if (validState || validCity) {
      setFormData({
        ...formData,
        state: validState || '',
        city: validCity || ''
      });
      setSelectedLocation(location);
      setIsMapModalOpen(false);
    }
  };

  const handleCustomLocationSelect = () => {
    // Only add if search term is not empty or just spaces
    const trimmedSearchTerm = mapSearchTerm.trim();
    if (trimmedSearchTerm) {
      const customLocation = {
        state: trimmedSearchTerm,
        city: trimmedSearchTerm
      };
      setFormData({
        ...formData,
        state: trimmedSearchTerm,
        city: ''
      });
      setSelectedLocation(customLocation);
      setIsMapModalOpen(false);
      setMapSearchTerm('');
    }
  };

  // Get locations for selected country
  const currentCountryLocations = locationsByCountry[formData.countryId] || [];
  
  
  const filteredLocations = Array.isArray(currentCountryLocations) ? currentCountryLocations.filter(location => {
    if (!mapSearchTerm) return true;
    if (!location) return false;
    
    const searchLower = mapSearchTerm.toLowerCase();
    return (
      (location.state && typeof location.state === 'string' && location.state.toLowerCase().includes(searchLower)) ||
      (location.city && typeof location.city === 'string' && location.city.toLowerCase().includes(searchLower)) ||
      (location.district && typeof location.district === 'string' && location.district.toLowerCase().includes(searchLower)) ||
      (Array.isArray(location.keywords) && location.keywords.some(keyword => 
        keyword && typeof keyword === 'string' && keyword.includes(searchLower)
      ))
    );
  }) : [];
  
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim() || !formData.password.trim() || !formData.mobileNumber.trim() || !formData.state.trim() || !formData.city.trim() || !formData.countryId) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Strong password validation
    const isStrongPassword = 
      formData.password.length >= 8 &&
      /[a-zA-Z]/.test(formData.password) &&
      /[0-9]/.test(formData.password) &&
      /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password);
    
    if (!isStrongPassword) {
      setError('Password must be at least 8 characters with letters, numbers, and special characters');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Clean mobile number - remove country code and leading zero
      let cleanMobileNumber = formData.mobileNumber.trim();
      
      // Remove country code if present (e.g., +92, +1, etc.)
      const countryCodeRegex = /^\+[0-9]{1,4}\s*/;
      cleanMobileNumber = cleanMobileNumber.replace(countryCodeRegex, '');
      
      // Remove leading zero for Pakistan numbers
      if (formData.countryId === 1 && cleanMobileNumber.startsWith('0')) {
        cleanMobileNumber = cleanMobileNumber.substring(1);
      }
      
      // Create clean form data
      const cleanFormData = {
        ...formData,
        mobileNumber: cleanMobileNumber,
        isTermsCondition: true,
        ...(referralCode ? { referralCode } : {}),
      };
      

      
      // Call the registration API
      const response = await apiService.register(cleanFormData);
      
      if (response.success) {
        // Store registration state (in real app, store JWT token)
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userEmail', formData.email);
        localStorage.setItem('userName', `${formData.firstName} ${formData.lastName}`);
        // Force page reload to update App component state
        window.location.href = '/';
      } else {
        // Show specific error message from API
        const errorMessage = response.error || response.message || 'Registration failed';
        setError(errorMessage);
      }
    } catch (error) {
      // Error handling
      setError(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageLayout scrollable>
      <div className="flex w-full max-w-[480px] flex-col items-center py-4 lg:py-0">
        <div className="w-full rounded-2xl border border-gray-100 bg-white px-6 py-7 shadow-[0_8px_30px_rgba(0,0,0,0.06)] sm:px-8 sm:py-8">
          <div className="mb-6 text-center">
            <h2 className="text-[1.65rem] font-bold tracking-tight text-gray-900">{t.createAccount}</h2>
            <p className="mt-1.5 text-sm text-gray-500">{t.joinAffiliateProgram}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2.5">
                  <p className="text-xs text-red-600">{error}</p>
                </div>
              )}

              {/* First Name and Last Name Fields */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label htmlFor="firstName" className={labelClass}>
                    {t.firstName || 'First Name'}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <User className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder={t.firstName || 'First Name'}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="lastName" className={labelClass}>
                    {t.lastName || 'Last Name'}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <User className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder={t.lastName || 'Last Name'}
                    />
                  </div>
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="reg-email" className={labelClass}>
                  {t.emailAddress || t.email || 'Email Address'}
                </label>
                <EmailDomainSuggestInput
                  id="reg-email"
                  name="email"
                  value={formData.email}
                  onChange={(email) => setFormData((prev) => ({ ...prev, email }))}
                  placeholder={t.enterEmail || 'Email Address'}
                  autoComplete="email"
                  disabled={isLoading}
                  required
                  leftIcon={<Mail className="h-4 w-4" />}
                />
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label htmlFor="password" className={labelClass}>
                    {t.password}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className={inputWithIconRightClass}
                      placeholder={t.password}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-2.5 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400 transition-colors hover:text-gray-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400 transition-colors hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  
                  {/* Password Strength Indicators */}
                  {formData.password && !(passwordStrength.length && passwordStrength.hasLetter && passwordStrength.hasNumber && passwordStrength.hasSpecial) && (
                    <div className="mt-1 space-y-1">
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${passwordStrength.length ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className={`text-xs ${passwordStrength.length ? 'text-green-600' : 'text-gray-500'}`}>
                          {t.atLeast8Characters}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${passwordStrength.hasLetter ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className={`text-xs ${passwordStrength.hasLetter ? 'text-green-600' : 'text-gray-500'}`}>
                          {t.containsLetter}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${passwordStrength.hasNumber ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className={`text-xs ${passwordStrength.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                          {t.containsNumber}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${passwordStrength.hasSpecial ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className={`text-xs ${passwordStrength.hasSpecial ? 'text-green-600' : 'text-gray-500'}`}>
                          {t.containsSpecialCharacter}
                        </span>
                      </div>
                    </div>
                  )}
                  
                </div>
                <div>
                  <label htmlFor="confirmPassword" className={labelClass}>
                    {t.confirm || 'Confirm'}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={inputWithIconRightClass}
                      placeholder={t.confirm || 'Confirm'}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-2.5 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400 transition-colors hover:text-gray-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400 transition-colors hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Country Field */}
              <div>
                <label htmlFor="countryId" className={labelClass}>
                  {t.country}
                </label>
                <div className="relative" ref={countryDropdownRef}>
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                    <Globe className="h-4 w-4 text-gray-400" />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIsCountryDropdownOpen(!isCountryDropdownOpen);
                      // Auto-focus search input when dropdown opens
                      setTimeout(() => {
                        if (countrySearchRef.current && !isCountryDropdownOpen) {
                          countrySearchRef.current.focus();
                        }
                      }, 100);
                    }}
                    className={`${inputClass} text-left`}
                  >
                    {isLoadingCountries ? t.loading : (Array.isArray(apiCountries) && apiCountries.length > 0 ? (formData.countryId ? apiCountries.find(c => c && (c.countryId || c.id) === formData.countryId)?.countryName : t.selectCountry) : t.noCountriesAvailable)}
                    <ChevronDown className={`absolute inset-y-0 right-0 pr-3 h-4 w-4 text-gray-400 transition-transform ${isCountryDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {isCountryDropdownOpen && (
                    <div className="absolute z-[9999] w-full mt-1 rounded-xl border border-gray-200 bg-white shadow-lg">
                      {/* Search Input */}
                      <div className="p-2 border-b border-gray-200">
                        <div className="relative">
                          <Search className="absolute inset-y-0 left-0 pl-2.5 h-4 w-4 text-gray-400" />
                          <input
                            ref={countrySearchRef}
                            type="text"
                            placeholder={t.searchCountries}
                            value={countrySearchTerm}
                            onChange={(e) => setCountrySearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                // Select first filtered country on Enter
                                if (Array.isArray(filteredCountries) && filteredCountries.length > 0) {
                                  const firstCountry = filteredCountries[0];
                                  handleCountryChange(firstCountry);
                                  setIsCountryDropdownOpen(false);
                                  setCountrySearchTerm('');
                                }
                              }
                            }}
                            className="block w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-8 pr-3 text-xs text-gray-900 focus:border-[#e11d23] focus:outline-none focus:ring-2 focus:ring-[#e11d23]/15"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      
                      {/* Countries List */}
                      <div 
                        className="max-h-48 overflow-y-auto"
                        style={{
                          scrollbarWidth: 'thin',
                          scrollbarColor: '#9CA3AF #F3F4F6'
                        }}
                      >
                        {Array.isArray(filteredCountries) && filteredCountries.length > 0 ? (
                          filteredCountries.map((country, index) => (
                            <button
                              key={country.countryId || country.id || `country-${index}`}
                              type="button"
                              onClick={() => handleCountryChange(country)}
                              className={`w-full px-3 py-2 text-xs text-left transition-colors text-gray-900 hover:bg-red-50 hover:text-[#e11d23] ${formData.countryId === (country.countryId || country.id) ? 'bg-red-50 text-[#e11d23]' : ''}`}
                            >
                              <div className="flex items-center justify-between">
                                <span>{country.countryName || country.name || 'Unknown Country'}</span>
                                <span className="text-xs text-gray-600">+{country.countryPhoneCode || country.phoneCode || country.code || 'N/A'}</span>
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-xs text-center text-gray-600">
                            {t.noCountriesFound}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Phone Number Field */}
              <div>
                <label htmlFor="mobileNumber" className={labelClass}>
                  {t.mobileNumber}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <Globe className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      name="countryCode"
                      value={formData.countryCode}
                      readOnly
                      className="block w-full rounded-xl border border-gray-200 bg-gray-100 py-2.5 pl-10 pr-2 text-sm text-gray-600"
                    />
                  </div>
                  <div className="col-span-2 relative">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <Phone className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="mobileNumber"
                      name="mobileNumber"
                      type="tel"
                      required
                      value={formData.mobileNumber}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder={t.mobileNumber}
                    />
                  </div>
                </div>
              </div>

              {/* Location Fields */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label htmlFor="state" className={labelClass}>
                    {t.stateProvince}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <Navigation className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="state"
                      name="state"
                      type="text"
                      required
                      value={formData.state}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder={t.stateProvince}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="city" className={labelClass}>
                    {t.city}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <MapPin className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      id="city"
                      name="city"
                      type="text"
                      required
                      value={formData.city}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder={t.city}
                    />
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="flex items-start space-x-2">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  required
                  className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 mt-0.5"
                />
                <label htmlFor="terms" className="text-xs leading-relaxed text-gray-600">
                  {t.agreeTo}{' '}
                  <button type="button" className="font-semibold text-[#e11d23] hover:text-red-700">{t.termsOfService}</button>
                  {' '}{t.and}{' '}
                  <button type="button" className="font-semibold text-[#e11d23] hover:text-red-700">{t.privacyPolicy}</button>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#e11d23] py-3.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(225,29,35,0.28)] transition hover:bg-[#c9171d] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-1.5">
                    <NewtonsCradleLoader size="24px" color="#ffffff" />
                    <span>{t.creatingAccount}</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1.5">
                    <span>{t.createAccount}</span>
                    <ArrowRight className="h-3 w-3" />
                  </div>
                )}
              </button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          {t.alreadyHaveAccount}{' '}
          <Link to="/login" className="font-semibold text-[#e11d23] hover:text-red-700">
            {t.signIn}
          </Link>
        </p>
      </div>

      {/* Map Modal */}
      {isMapModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsMapModalOpen(false)}
          />
          
          {/* Modal Content */}
          <div className="relative h-80 w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 p-3">
              <h3 className="text-sm font-semibold text-gray-900">{t.selectLocation}</h3>
              <button
                onClick={() => setIsMapModalOpen(false)}
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="flex h-64 flex-col p-3">
              <div className="mb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t.searchCitiesStates}
                    value={mapSearchTerm}
                    onChange={(e) => setMapSearchTerm(e.target.value)}
                    className="block w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-xs text-gray-900 placeholder-gray-500 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/15"
                    autoFocus
                  />
                </div>
              </div>

              <div className="flex-1 overflow-hidden">
                <div className="h-full overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#9CA3AF #F3F4F6' }}>
                  {filteredLocations.length > 0 ? (
                    <div className="space-y-1">
                      {filteredLocations.slice(0, 10).map((location, index) => (
                        <button
                          key={index}
                          onClick={() => handleMapLocationSelect(location)}
                          className="w-full rounded-lg border border-gray-200 bg-white p-2 text-left transition-colors hover:border-red-200 hover:bg-red-50"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xs font-medium text-gray-900">{location.city || 'Unknown City'}</div>
                              <div className="text-xs text-gray-600">{location.state || 'Unknown State'}</div>
                            </div>
                            <MapPin className="h-3 w-3 text-gray-400" />
                          </div>
                        </button>
                      ))}
                      {filteredLocations.length > 10 && (
                        <div className="py-2 text-center text-xs text-gray-500">
                          Showing first 10 results. Refine your search for more specific results.
                        </div>
                      )}
                    </div>
                  ) : mapSearchTerm ? (
                    <div className="space-y-3">
                      <div className="py-4 text-center">
                        <Search className="mx-auto mb-2 h-6 w-6 text-gray-400" />
                        <p className="text-xs text-gray-600">{t.noLocationsFoundFor} "{mapSearchTerm}"</p>
                      </div>
                      <button
                        onClick={handleCustomLocationSelect}
                        className="w-full rounded-lg border-2 border-dashed border-gray-300 bg-white p-2 text-left transition-colors hover:border-red-400 hover:bg-red-50"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs font-medium text-gray-900">{t.addAsCustomLocation} "{mapSearchTerm}"</div>
                            <div className="text-xs text-gray-600">{t.useAsStateProvince}</div>
                          </div>
                          <Plus className="h-3 w-3 text-gray-400" />
                        </div>
                      </button>
                    </div>
                  ) : Array.isArray(currentCountryLocations) && currentCountryLocations.length > 0 ? (
                    <div className="space-y-1">
                      <h4 className="mb-2 text-sm font-medium text-gray-700">{t.popularLocations}</h4>
                      {currentCountryLocations.slice(0, 8).map((location, index) => (
                        <button
                          key={index}
                          onClick={() => handleMapLocationSelect(location)}
                          className="w-full rounded-lg border border-gray-200 bg-white p-2 text-left transition-colors hover:border-red-200 hover:bg-red-50"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xs font-medium text-gray-900">{location.city || 'Unknown City'}</div>
                              <div className="text-xs text-gray-600">{location.state || 'Unknown State'}</div>
                            </div>
                            <MapPin className="h-3 w-3 text-gray-400" />
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <Map className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                      <p className="text-sm text-gray-600">{t.noPredefinedLocations}</p>
                      <p className="mt-1 text-xs text-gray-500">{t.manuallyTypeStateCity}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AuthPageLayout>
  );
};

export default Register;